import React, { useState } from 'react'
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { Edit, DeleteForever } from '@mui/icons-material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import { Colors } from '../styles/theme'
import * as Yup from 'yup'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { useAppContext } from '../context/AppProvider'
import { useUsers } from '../hooks/useUsers'
import DeleteConfirmationDialog from './DeleteConfirmationDialog'

const emptyCompanyProfile = {
  legal_name: '',
  registration_no: '',
  fiscal_address: '',
  registration_status: '',
  registration_date: '',
  county: '',
  city: '',
  postal_code: '',
  caen: ''
}

const emptyUserForm = {
  _id: -1,
  company: '',
  cui: '',
  email: '',
  username: '',
  phone: '',
  status: 'pending',
  company_profile: emptyCompanyProfile,
  company_verification: {
    status: 'pending_manual',
    lookup_status: 'not_checked'
  },
  anaf_last_checked_at: ''
}

const validationData = Yup.object().shape({
  company: Yup.string().required('Nume firmă is required'),
  cui: Yup.string().required('CUI is required'),
  status: Yup.string().required('Status is required'),
  email: Yup.string().required('Email is required'),
  username: Yup.string().required('Username is required')
})

const buildInitialValues = (user = {}) => {
  const companyProfile = user.company_profile || {}

  return {
    _id: user._id ?? -1,
    company: user.company || companyProfile.legal_name || '',
    cui: user.cui || '',
    email: user.email || '',
    username: user.username || '',
    phone: user.phone || '',
    status: user.status || 'pending',
    company_profile: {
      legal_name: companyProfile.legal_name || '',
      registration_no: companyProfile.registration_no || '',
      fiscal_address: companyProfile.fiscal_address || '',
      registration_status: companyProfile.registration_status || '',
      registration_date: companyProfile.registration_date || '',
      county: companyProfile.county || '',
      city: companyProfile.city || '',
      postal_code: companyProfile.postal_code || '',
      caen: companyProfile.caen || ''
    },
    company_verification: user.company_verification || {
      status: 'pending_manual',
      lookup_status: 'not_checked'
    },
    anaf_last_checked_at: user.anaf_last_checked_at || ''
  }
}

const buildSubmitPayload = (values) => {
  const company = (values.company || '').trim()
  const legalName = (values.company_profile?.legal_name || '').trim()

  return {
    _id: values._id,
    company,
    cui: (values.cui || '').trim(),
    email: (values.email || '').trim(),
    username: (values.username || '').trim(),
    phone: (values.phone || '').trim(),
    status: values.status,
    company_profile: {
      legal_name: legalName,
      registration_no: (values.company_profile?.registration_no || '').trim(),
      fiscal_address: (values.company_profile?.fiscal_address || '').trim(),
      registration_status: (values.company_profile?.registration_status || '').trim(),
      registration_date: (values.company_profile?.registration_date || '').trim(),
      county: (values.company_profile?.county || '').trim(),
      city: (values.company_profile?.city || '').trim(),
      postal_code: (values.company_profile?.postal_code || '').trim(),
      caen: (values.company_profile?.caen || '').trim()
    }
  }
}

const verificationLabel = (verification = {}) => {
  return verification?.status === 'verified_auto' ? 'Automat' : 'Manual'
}

const verificationColor = (verification = {}) => {
  return verification?.status === 'verified_auto' ? 'success' : 'warning'
}

const formatTimestamp = (value) => {
  if (!value) {
    return 'Niciodată'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return String(value)
  }

  return parsed.toLocaleString('ro-RO')
}

const Users = () => {
  const { state } = useAppContext()
  const { handleUpdateUserProfile, handleDeleteUser, handleApproveUser, handleRecheckUserCompany } = useUsers()
  const users = state.users.data
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [open, setOpen] = useState(false)
  const [initialValues, setInitialValues] = useState(emptyUserForm)
  const [dialogMessage, setDialogMessage] = useState(null)
  const [isRechecking, setIsRechecking] = useState(false)

  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setUserToDelete(null)
  }

  const handleConfirmDelete = () => {
    if (userToDelete) {
      handleDeleteUser(userToDelete._id)
    }
    handleCloseDeleteDialog()
  }

  const handleApprove = (user) => {
    const payload = { ...user, status: 'active' }
    handleApproveUser(payload)
  }

  const handleEdit = (user) => {
    setDialogMessage(null)
    setInitialValues(buildInitialValues(user))
    setOpen(true)
  }

  const handleSubmit = async (values) => {
    const payload = buildSubmitPayload(values)
    setDialogMessage(null)
    try {
      const updated = await handleUpdateUserProfile(payload)
      setInitialValues(buildInitialValues(updated || payload))
      setDialogMessage({ type: 'success', text: 'Datele utilizatorului au fost salvate.' })
      setOpen(false)
    } catch (error) {
      console.error(error)
      setDialogMessage({ type: 'error', text: 'Nu am putut salva modificările utilizatorului.' })
    }
  }

  const handleRecheck = async (userId, setValues) => {
    if (!userId || userId === -1) {
      return
    }

    setDialogMessage(null)
    setIsRechecking(true)
    try {
      const refreshed = await handleRecheckUserCompany(userId)
      if (refreshed && setValues) {
        setValues(buildInitialValues(refreshed))
      }
      setDialogMessage({ type: 'success', text: 'Datele firmei au fost reverificate după CUI.' })
    } catch (error) {
      console.error(error)
      setDialogMessage({ type: 'error', text: 'Nu am putut reverifica firma după CUI.' })
    } finally {
      setIsRechecking(false)
    }
  }

  const renderCompany = (user) => {
    return user.company || user.company_profile?.legal_name || (
      user.company_verification?.status === 'pending_manual' ? 'În verificare manuală' : ''
    )
  }

  return (
    <>
      <Typography variant='h6' sx={{ marginBottom: '20px' }}>Utilizatori</Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nume firmă</TableCell>
              <TableCell>CUI</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Verificare CUI</TableCell>
              <TableCell>Acțiuni</TableCell>
              <TableCell>Aprobare</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(user => (
              <TableRow key={user._id}>
                <TableCell>{renderCompany(user)}</TableCell>
                <TableCell>{user.cui}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.phone}</TableCell>
                <TableCell>{user.status}</TableCell>
                <TableCell>
                  <Chip
                    label={verificationLabel(user.company_verification)}
                    size='small'
                    color={verificationColor(user.company_verification)}
                    variant='outlined'
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(user)} title='Editează utilizator'>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleOpenDeleteDialog(user)} title='Șterge utilizator'>
                    <DeleteForever sx={{ color: Colors.accent.main }}/>
                  </IconButton>
                  {user.company_verification?.status !== 'verified_auto' && (
                    <IconButton
                      onClick={() => handleRecheck(user._id)}
                      title='Reverifică firmă după CUI'
                      disabled={isRechecking}
                    >
                      <AutorenewIcon color='primary' />
                    </IconButton>
                  )}
                </TableCell>
                <TableCell>
                  <IconButton title='Aprobă utilizator'>
                    <CheckCircleOutlineIcon color='success' onClick={() => handleApprove(user)}/>
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} fullWidth maxWidth='lg'>
        <DialogTitle>
          {'Modifică datele utilizatorului'}
        </DialogTitle>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationData}
          onSubmit={handleSubmit}
        >
          {({ dirty, isValid, values, getFieldProps, setFieldValue, setValues }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Alert severity='info'>
                      Poți reverifica automat datele firmei după CUI sau poți completa manual datele comerciale folosite la facturare.
                    </Alert>
                  </Grid>

                  {dialogMessage && (
                    <Grid item xs={12}>
                      <Alert severity={dialogMessage.type}>{dialogMessage.text}</Alert>
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle2' sx={{ marginBottom: '8px' }}>Verificare curentă</Typography>
                    <Chip
                      label={verificationLabel(values.company_verification)}
                      color={verificationColor(values.company_verification)}
                      sx={{ marginRight: '8px' }}
                    />
                    <Typography variant='body2' sx={{ marginTop: '8px' }}>
                      Ultima reverificare: {formatTimestamp(values.anaf_last_checked_at)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Button
                      variant='outlined'
                      color='primary'
                      startIcon={<AutorenewIcon />}
                      onClick={() => handleRecheck(getFieldProps('_id').value, setValues)}
                      disabled={!values.cui || getFieldProps('_id').value === -1 || isRechecking}
                    >
                      {isRechecking ? 'Reverificăm...' : 'Reverifică după CUI'}
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name='company'
                      label='Nume firmă'
                      required
                      fullWidth
                    />
                    <ErrorMessage name='company'>
                      {(message) => (
                        <Typography color='red'>{message}</Typography>
                      )}
                    </ErrorMessage>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name='cui'
                      label='CUI'
                      required
                      fullWidth
                    />
                    <ErrorMessage name='cui'>
                      {(message) => (
                        <Typography color='red'>{message}</Typography>
                      )}
                    </ErrorMessage>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name='email'
                      label='Email'
                      required
                      fullWidth
                    />
                    <ErrorMessage name='email'>
                      {(message) => (
                        <Typography color='red'>{message}</Typography>
                      )}
                    </ErrorMessage>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name='username'
                      label='Username'
                      required
                      fullWidth
                    />
                    <ErrorMessage name='username'>
                      {(message) => (
                        <Typography color='red'>{message}</Typography>
                      )}
                    </ErrorMessage>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name='phone'
                      label='Nr. de telefon'
                      fullWidth
                    />
                    <ErrorMessage name='phone'>
                      {(message) => (
                        <Typography color='red'>{message}</Typography>
                      )}
                    </ErrorMessage>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name='company_profile.legal_name'
                      label='Denumire legală'
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name='company_profile.registration_no'
                      label='Nr. Reg. Com.'
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name='company_profile.caen'
                      label='CAEN'
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name='company_profile.registration_status'
                      label='Status registru'
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      name='company_profile.city'
                      label='Localitate'
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      name='company_profile.county'
                      label='Județ'
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      name='company_profile.postal_code'
                      label='Cod poștal'
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      name='company_profile.fiscal_address'
                      label='Adresă fiscală'
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl
                      sx={{
                        border: 'solid 1px',
                        borderColor: Colors.borderColor.main,
                        borderRadius: '5px',
                        padding: '10px'
                      }}
                      fullWidth
                    >
                      <FormLabel id='user-status-radio-group-label' sx={{ fontSize: '12px' }}>Status utilizator</FormLabel>
                      <Grid container spacing={1} sx={{ marginTop: '6px' }}>
                        {[
                          { label: 'Active', value: 'active' },
                          { label: 'Pending', value: 'pending' },
                          { label: 'Inactive', value: 'inactive' }
                        ].map((option) => (
                          <Grid item key={option.value}>
                            <Button
                              type='button'
                              variant={values.status === option.value ? 'contained' : 'outlined'}
                              color={values.status === option.value ? 'primary' : 'inherit'}
                              onClick={() => setFieldValue('status', option.value)}
                            >
                              {option.label}
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                    </FormControl>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  disabled={!dirty || !isValid}
                  type='submit'
                  variant='contained'
                  color='primary'
                >
                  {getFieldProps('_id').value !== -1 ? 'Save Edit' : 'Save'}
                </Button>
                <Button autoFocus variant='contained' onClick={() => setOpen(false)}>Cancel</Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        object={userToDelete ? userToDelete.username : ''}
      />
    </>
  )
}

export default Users
